import React, {
  useCallback,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import {
  Drawer,
  Form,
  Row,
} from 'antd';

import DrawerFooter from '../../common/containers/DrawerFooter';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import MaterialInfo from '../../materials/MaterialInfo';
import {
  getMarkupPercent,
  getDerivedPriceValue,
  getUpdatedPricingFields,
} from '../../materials/materialsHelpers';
import { parsePhaseCostcodeCategoryKey } from '../../helpers/costcodeHelpers';

const PROJECT_MATERIAL_FORM_FIELDS = [
  'name',
  'description',
  'partNumber',
  'supplier',
  'vendorId',
  'units',
  'cost',
  'markup',
  'labourCost',
  'labourMarkup',
  'costcodeId',
  'phaseId',
  'divisionId',
  'code',
];

export default function ProjectMaterialDrawer({
  visible,
  onClose,
  onSubmit,
  onDelete,
  item,
  divisionId,
  projectId,
  isNotDisplay = true,
}) {
  const [form] = Form.useForm();

  const title = item?.name
    ? (isNotDisplay ? 'Edit Material' : item.name)
    : 'Add Material';

  const canDelete = isNotDisplay && item?.id && onDelete;

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      return;
    }

    const materialMarkup = item?.markup || 0;
    const materialLabourMarkup = item?.labourMarkup || 0;
    const fieldValues = {
      divisionId: item?.divisionId || divisionId,
      markup: getMarkupPercent(materialMarkup) ?? 0,
      labourMarkup: getMarkupPercent(materialLabourMarkup) ?? 0,
      price: getDerivedPriceValue(item?.cost, materialMarkup),
      labourPrice: getDerivedPriceValue(item?.labourCost, materialLabourMarkup),
    };

    PROJECT_MATERIAL_FORM_FIELDS.forEach((key) => {
      if (item?.[key] !== undefined && fieldValues[key] === undefined) {
        fieldValues[key] = item[key];
      }
    });

    if (item?.costcodeId) {
      fieldValues.phaseCostcodeKey = `${item.phaseId || 'unphased'}.${item.costcodeId}`;
    }

    form.setFieldsValue(fieldValues);
  }, [visible, item, divisionId, form]);

  const onValuesChange = useCallback((changedValues, allValues) => {
    const pricingUpdates = {
      ...getUpdatedPricingFields({
        changedValues,
        allValues,
        priceKey: 'price', // these are defaults, just here for clarity
        markupKey: 'markup',
        costKey: 'cost',
      }),
      ...getUpdatedPricingFields({
        changedValues,
        allValues,
        priceKey: 'labourPrice',
        markupKey: 'labourMarkup',
        costKey: 'labourCost',
      }),
    };
    if (Object.keys(pricingUpdates).length) {
      form.setFieldsValue(pricingUpdates);
    }
  }, [form]);

  const onSubmitClicked = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const payload = {};
      PROJECT_MATERIAL_FORM_FIELDS.forEach((key) => {
        if (values[key] !== undefined) {
          payload[key] = values[key];
        }
      });

      const { phaseId, costcodeId } = parsePhaseCostcodeCategoryKey(values.phaseCostcodeKey);
      payload.costcodeId = costcodeId;
      payload.phaseId = phaseId === 'unphased' ? null : phaseId;

      onSubmit({
        ...payload,
        markup: payload.markup ? payload.markup / 100 : payload.markup,
        labourMarkup: payload.labourMarkup ? payload.labourMarkup / 100 : payload.labourMarkup,
        divisionId: payload.divisionId || divisionId,
      });
    } catch (err) {
      // validation failed
    }
  }, [form, onSubmit, divisionId]);

  return (
    <Drawer
      title={title}
      width={800}
      onClose={onClose}
      visible={visible}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={onValuesChange}
        style={{ paddingBottom: 80 }}
      >
        <MaterialInfo
          item={item}
          isNotDisplay={isNotDisplay}
          divisionId={item?.divisionId || divisionId}
          totalQuantity={0}
          totalQuantityAllocated={0}
          shouldAutoCalcCost
          shouldAutoCalcLabourCost
          projectId={projectId}
        />
      </Form>
      {isNotDisplay && (
        <DrawerFooter>
          <Row justify="space-between" align="middle" style={{ width: '100%' }}>
            <div>
              {canDelete && (
                <OnTraccrButton
                  title="Delete"
                  type="back"
                  onClick={onDelete}
                />
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <OnTraccrButton
                title="Cancel"
                type="cancel"
                onClick={onClose}
              />
              <OnTraccrButton
                title="Save"
                onClick={onSubmitClicked}
              />
            </div>
          </Row>
        </DrawerFooter>
      )}
    </Drawer>
  );
}

ProjectMaterialDrawer.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  item: PropTypes.shape({}),
  divisionId: PropTypes.string,
  projectId: PropTypes.string,
  isNotDisplay: PropTypes.bool,
};

ProjectMaterialDrawer.defaultProps = {
  visible: false,
  onDelete: undefined,
  item: null,
  divisionId: undefined,
  projectId: undefined,
  isNotDisplay: true,
};
